import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Server } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetFooter,
  BottomSheetTitle,
  BottomSheetDescription,
} from '@/components/ui/bottom-sheet';
import { formatDate, cn } from '@/lib/utils';
import { Plus, RefreshCw, Trash2, Activity, Server as ServerIcon, HardDrive, Cpu } from 'lucide-react';

export function Servers() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteSheet, setDeleteSheet] = useState<Server | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 22,
    username: 'root',
    maxInstances: 50,
  });
  
  const queryClient = useQueryClient();

  const { data: servers, isLoading, refetch } = useQuery({
    queryKey: ['servers'],
    queryFn: adminApi.getServers,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminApi.createServer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      setCreateDialogOpen(false);
      setFormData({ name: '', host: '', port: 22, username: 'root', maxInstances: 50 });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteServer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      setDeleteSheet(null);
    },
  });

  const healthCheckMutation = useMutation({
    mutationFn: (id: string) => adminApi.checkServerHealth(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
  });

  const healthCheckAllMutation = useMutation({
    mutationFn: () => adminApi.checkAllServersHealth(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Servers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your infrastructure</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => healthCheckAllMutation.mutate()}
            disabled={healthCheckAllMutation.isPending}
            className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all disabled:opacity-50"
          >
            <RefreshCw className={cn("h-5 w-5 text-muted-foreground", healthCheckAllMutation.isPending && "animate-spin")} />
          </button>
          <button 
            onClick={() => setCreateDialogOpen(true)} 
            className="p-3 rounded-xl bg-foreground text-background active:scale-95 transition-all"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
        </div>
      ) : servers?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="h-20 w-20 rounded-full bg-muted/30 flex items-center justify-center mb-4">
            <ServerIcon className="h-10 w-10 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-medium mb-1">No servers yet</h3>
          <p className="text-sm text-muted-foreground mb-6">Add your first server to get started</p>
          <Button size="lg" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-5 w-5" />
            Add Server
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {servers?.map((server: Server) => (
            <ServerCard
              key={server.id}
              server={server}
              onHealthCheck={() => healthCheckMutation.mutate(server.id)}
              onDelete={() => setDeleteSheet(server)}
              isChecking={healthCheckMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Create Server Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Server</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Production Server 1"
                required
                className="mt-1.5 h-12"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Host</label>
              <Input
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                placeholder="192.168.1.100"
                required
                className="mt-1.5 h-12"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Port</label>
                <Input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  className="mt-1.5 h-12"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Username</label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="mt-1.5 h-12"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Max Instances</label>
              <Input
                type="number"
                value={formData.maxInstances}
                onChange={(e) => setFormData({ ...formData, maxInstances: parseInt(e.target.value) })}
                className="mt-1.5 h-12"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="secondary" size="lg" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="lg" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Server'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Bottom Sheet */}
      <BottomSheet open={!!deleteSheet} onOpenChange={() => setDeleteSheet(null)}>
        <BottomSheetContent>
          <BottomSheetHeader>
            <BottomSheetTitle>Delete Server</BottomSheetTitle>
            <BottomSheetDescription>
              Are you sure you want to delete "{deleteSheet?.name}"? This action cannot be undone.
            </BottomSheetDescription>
          </BottomSheetHeader>
          <BottomSheetFooter>
            <Button
              size="xl"
              variant="destructive"
              onClick={() => deleteSheet && deleteMutation.mutate(deleteSheet.id)}
              disabled={deleteMutation.isPending || (deleteSheet?.currentInstances || 0) > 0}
              className="w-full"
            >
              {deleteMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Server
                </>
              )}
            </Button>
            {(deleteSheet?.currentInstances || 0) > 0 && (
              <p className="text-xs text-status-error-foreground text-center">
                Cannot delete server with active instances
              </p>
            )}
            <Button
              size="xl"
              variant="secondary"
              onClick={() => setDeleteSheet(null)}
              className="w-full"
            >
              Cancel
            </Button>
          </BottomSheetFooter>
        </BottomSheetContent>
      </BottomSheet>
    </div>
  );
}

interface ServerCardProps {
  server: Server;
  onHealthCheck: () => void;
  onDelete: () => void;
  isChecking: boolean;
}

function ServerCard({ server, onHealthCheck, onDelete, isChecking }: ServerCardProps) {
  const statusColors = {
    active: 'status-dot-running',
    maintenance: 'status-dot-pending',
    offline: 'status-dot-error',
  };

  const instancePercent = (server.currentInstances / server.maxInstances) * 100;
  const memoryPercent = server.usedMemoryMb && server.totalMemoryMb 
    ? (server.usedMemoryMb / server.totalMemoryMb) * 100 
    : 0;

  return (
    <div className="mobile-card space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-muted/50 flex items-center justify-center">
            <ServerIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-medium">{server.name}</h3>
            <p className="text-xs text-muted-foreground font-mono">{server.host}:{server.port}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={cn("status-dot", statusColors[server.status as keyof typeof statusColors] || 'status-dot-stopped')} />
          <Badge variant={server.status === 'active' ? 'success' : server.status === 'offline' ? 'destructive' : 'warning'}>
            {server.status}
          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Cpu className="h-4 w-4" />
              Instances
            </span>
            <span className="font-medium">{server.currentInstances} / {server.maxInstances}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-500",
                instancePercent > 80 ? "bg-status-error-foreground" : "bg-foreground/70"
              )}
              style={{ width: `${instancePercent}%` }}
            />
          </div>
        </div>

        {server.usedMemoryMb && server.totalMemoryMb && (
          <div>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="flex items-center gap-2 text-muted-foreground">
                <HardDrive className="h-4 w-4" />
                Memory
              </span>
              <span className="font-medium">
                {Math.round(server.usedMemoryMb / 1024)}GB / {Math.round(server.totalMemoryMb / 1024)}GB
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  memoryPercent > 80 ? "bg-status-error-foreground" : "bg-foreground/70"
                )}
                style={{ width: `${memoryPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Last check: {server.lastHealthCheck ? formatDate(server.lastHealthCheck) : 'Never'}
      </p>

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t border-border">
        <Button
          variant="secondary"
          size="lg"
          className="flex-1"
          onClick={onHealthCheck}
          disabled={isChecking}
        >
          <Activity className={cn("h-4 w-4 mr-2", isChecking && "animate-spin")} />
          Check
        </Button>
        <Button
          variant="outline"
          size="icon-lg"
          onClick={onDelete}
          disabled={server.currentInstances > 0}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
